import { useEffect, useState } from "react";
import { useSEO } from "@/contexts/SeoContext.tsx";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { useMe, useReferrals } from "@/data/user.ts";
import Link from "@/components/refer-a-friend/Link";
import Button from "@edusogno/primitives/button";
import Banner from "@/components/refer-a-friend/Banner";
import Progress from "@/components/refer-a-friend/Progress";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import ReferXIcon from "@/components/icons/share/ReferXIcon";
import PageHeading from "@/components/ui/PageHeading.tsx";
import useClipboard from "@/utils/hooks/useClipboard.ts";
import ReferEmailIcon from "@/components/icons/share/ReferEmailIcon";
import ReferOtherIcon from "@/components/icons/share/ReferOtherIcon";
import ReferWhatsappIcon from "@/components/icons/share/ReferWhatsappIcon";
import ReferLinkedInIcon from "@/components/icons/share/ReferLinkedInIcon";
import ReferFacebookIcon from "@/components/icons/share/ReferFacebookIcon";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";

export default function ReferAFriend() {
  const { me } = useMe();

  const [signUp, setSignUp] = useState<number>(0);

  const { referrals } = useReferrals();

  const { t } = useTranslation();

  const { setTitle, setDescription } = useSEO();
  const { setGlobalState } = useGlobals();

  const referralUrl: string =
    "https://edusogno.com/form/referral/" + me?.item.user.token;

  const { copyToClipboard, isCopiedToClipboard } = useClipboard();

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-background");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTitle(
      `${me?.item.user.first_name} ${me?.item.user.last_name} | Refer a Friend`,
    );
    setDescription(
      "Share your journey with friends and gain new opportunities to enhance your English",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    if (isCopiedToClipboard) {
      enqueueSnackbar("Copied to clipboard", {
        variant: "info",
      });
    }
  }, [isCopiedToClipboard]);

  useEffect(() => {
    if (referrals && referrals.items) {
      let converted = 0;
      referrals.items.forEach((element) => {
        if (element.converted) {
          converted++;
        }
      });
      setSignUp(converted);
    }
  }, [referrals]);

  const handleShare = (link: string): void => {
    try {
      navigator
        .share({
          title: `${t("refer_a_friend.share_link.socials")}`,
          url: link || referralUrl,
        })
        .then(() => {})
        .catch((error) => {
          console.error("Error sharing:", error);
        });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const socialMediaOutlets = [
    {
      icon: ReferEmailIcon,
      text: "Share via E-mail",
      // link: `mailto:?subject=Migliora il tuo inglese con Edusogno! &body=Fai come me e migliora l'inglese con Edusogno. Prenota un incontro con il loro team per sapere come funziona con questo link ${referralUrl}`,
      link: `mailto:?subject=${t(
        "refer_a_friend.share_link.email",
      )} ${referralUrl}`,
    },
    {
      icon: ReferWhatsappIcon,
      text: "Share via WhatsApp",
      // link: `https://api.whatsapp.com/send?text=Fai come me e migliora l'inglese con Edusogno. Prenota un incontro con il loro team per sapere come funziona ${referralUrl}`,
      link: `https://api.whatsapp.com/send?text=${t(
        "refer_a_friend.share_link.socials",
      )} ${referralUrl}`,
    },
    {
      icon: ReferLinkedInIcon,
      text: "Share via LinkedIn",
      // link: `https://www.linkedin.com/shareArticle?url=${referralUrl}&title=Fai come me e migliora l'inglese con Edusogno. Prenota un incontro con il loro team per sapere come funziona`,
      link: `https://www.linkedin.com/shareArticle?url=${referralUrl}&title=${t(
        "refer_a_friend.share_link.socials",
      )}`,
    },
    {
      icon: ReferFacebookIcon,
      text: "Share via Facebook",
      link: `https://www.facebook.com/sharer.php?u=${referralUrl}`,
    },
    {
      icon: ReferXIcon,
      text: "Share via X",
      // link: `http://twitter.com/share?text=Fai come me e migliora l'inglese con Edusogno. Prenota un incontro con il loro team per sapere come funziona ${referralUrl}`,
      link: `http://twitter.com/share?text=${t(
        "refer_a_friend.share_link.socials",
      )} ${referralUrl}`,
    },
    {
      icon: ReferOtherIcon,
      text: "Other",
      link: "",
    },
  ];

  return (
    <DashboardContentLayout className="overflow-y-auto mb-2">
      <PageHeading
        title={t("menu.refer")}
        subtitle={t("refer_a_friend.sub_header")}
        titleClassName="!text-light"
        subtitleClassName="text-light"
      />

      <div className="flex flex-col justify-between h-full gap-6 mt-6">
        <Banner />

        <div className="flex flex-col gap-6 flex-grow">
          <div className="flex flex-col lg:flex-row items-center gap-7">
            <Link
              url={referralUrl}
              className="lg:flex-[1_1_calc(100%_-_355px)]]"
              copyToClipboard={copyToClipboard}
            />

            <div className="flex flex-row items-center lg:gap-4 gap-2 lg:flex-[0_0_355px] lg:max-w-[355px]">
              {socialMediaOutlets.map((r, idx) => {
                const Icon = r.icon;

                const handleClick = () => {
                  if (r.text === "Other") {
                    handleShare(r.link);
                  } else if (r.link) {
                    window.open(r.link, "_blank");
                  }
                };

                return (
                  <Button
                    key={"social-" + idx}
                    title={r.text}
                    variant="ghost"
                    onClick={handleClick}
                    className="w-fit min-w-fit"
                  >
                    <Icon />
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="p-[min(5%,2rem)] bg-white rounded-md w-full h-fit">
            <div className="flex-col flex h-full gap-6 justify-center">
              <div className="flex flex-col w-full gap-2">
                <h4 className="text-2xl font-semibold text-[--primary] leading-[100%]">
                  {/* Unlock your rewards */}
                  {t("refer_a_friend.progress.title")}
                </h4>

                <p className="text-[12px] font-normal text-[--secondary] leading-[100%]">
                  {/* Bring your friends to Edusogno, every friend who signs up will allow you to unlock fantastic rewards */}
                  {t("refer_a_friend.progress.subtitle")}
                </p>
              </div>

              <Progress numberOfFriends={signUp} />
            </div>
          </div>
        </div>
      </div>
    </DashboardContentLayout>
  );
}
